/**
 * 오더 상세 페이지 (관리자용)
 * - 차량 정보, 검차 결과, 도막 맵
 * - 매입 결정 → AUTODOC 연동
 * - 평가사 리뷰 작성
 */
import React, { useState, useEffect, useMemo } from 'react';
import { API, API_ORIGIN, authFetch } from '../utils/api';
import { StatusBadge, LevelBadge, Stars, InfoRow } from './CommonUI';
import { EvalDamageSection, EvalPaintSection } from './EvalCarDiagram';
import InspectionPhotos from './InspectionPhotos';
import CustomModal from './CustomModal';
import PhotoLightbox from './PhotoLightbox';
import EvalDecideModal from './EvalDecideModal';
import { ORDER_STATUS, formatMoney } from '../utils/constants';
import useEvalThumbBlobs from '../utils/useEvalThumbBlobs';

function useIsMobile(bp) { var b = bp || 768; var [m, setM] = useState(window.innerWidth <= b); useEffect(function() { var h = function() { setM(window.innerWidth <= b); }; window.addEventListener('resize', h); return function() { window.removeEventListener('resize', h); }; }, [b]); return m; }

export default function OrderDetail({ order, evalResult, inspectorReview, inspector, onBack, onStatusChange, onSubmitReview, onPurchase }) {
  var isMobile = useIsMobile();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ speed: 0, accuracy: 0, detail: 0, overall: 0, comment: '' });
  const [showDecideModal, setShowDecideModal] = useState(false);
  var [showContractForm, setShowContractForm] = useState(false);
  var [contractPrice, setContractPrice] = useState('');
  // 사진 라이트박스 상태는 PhotoLightbox 내부 — viewPhotoIndex만 부모가 관리
  var [viewPhotoIndex, setViewPhotoIndex] = useState(null);
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };

  // 전체 사진 배열 (useMemo 로 안정화 — useEvalThumbBlobs dep 흔들림 최소화)
  // 🔴 SECURITY (Wave 1B, 2026-04-29): thumbBlobs map 으로 인증 fetch — 정적 URL 직접 참조 제거.
  var allPhotos = useMemo(function() {
    var p = evalResult && evalResult.photos;
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch (e) { p = []; } }
    if (!Array.isArray(p)) return [];
    return p.filter(function(x) { return x && x.url && !x.url.startsWith('blob:'); });
  }, [evalResult]);
  var thumbBlobs = useEvalThumbBlobs(allPhotos);

  const evalLabels = ['외관', '내관', '엔진', '하부', '종합'];
  const evalKeys = ['score_exterior', 'score_interior', 'score_engine', 'score_undercarriage', 'score_overall'];

  // 매입 결정 성공 콜백 (모달이 API 직접 호출)
  const handleDecideSuccess = () => {
    setShowDecideModal(false);
    if (onPurchase) onPurchase(order.id, null); // 부모 리페치 트리거용
  };

  // 리뷰 제출
  const handleReview = () => {
    if (!reviewForm.overall) { setModal({ type: 'danger', title: '실패', message: '종합 평점을 선택해주세요', hideCancel: true, onConfirm: closeModal }); return; }
    onSubmitReview(order.id, order.inspector_id, reviewForm);
    setShowReviewForm(false);
  };

  // 현장 계약서 출력
  var printContract = function() {
    if (!contractPrice) { setModal({ type: 'danger', title: '실패', message: '판매금액을 입력해주세요', hideCancel: true, onConfirm: closeModal }); return; }
    var now = new Date();
    var yy = now.getFullYear();
    var mo = now.getMonth() + 1;
    var dy = now.getDate();
    var priceNum = Number(contractPrice);
    var priceStr = priceNum.toLocaleString();
    var html = '<!DOCTYPE html><html><head><meta charset="utf-8">'
      + '<style>'
      + '@page{size:A4;margin:15mm}'
      + '@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}'
      + 'body{font-family:"맑은 고딕","Malgun Gothic",Pretendard,sans-serif;font-size:11px;color:#000;margin:0;padding:20px 28px;line-height:1.6}'
      + 'table{border-collapse:collapse;width:100%}'
      + 'td,th{border:1px solid #333;padding:7px 10px;font-size:11px;vertical-align:middle}'
      + '.lbl{background:#f5f5f5;font-weight:700;text-align:center;letter-spacing:3px;width:22%}'
      + '.ptbl td,.ptbl th{padding:6px 10px;font-size:11px}'
      + '.ptbl .pl{background:#fafafa;text-align:center;width:110px;letter-spacing:2px;font-size:10.5px;font-weight:600}'
      + '</style></head><body>'
      + '<h1 style="font-size:18px;text-align:center;letter-spacing:10px;margin:8px 0 24px;font-weight:700">수출용 자동차 매매 계약서</h1>'
      + '<table style="margin-bottom:14px"><tbody>'
      + '<tr><th class="lbl">자동차명</th><td style="font-size:13px;font-weight:700">' + (order.car_model || '') + ' ' + (order.car_detail || '') + '</td>'
      + '<th class="lbl">자동차등록번호</th><td style="font-size:14px;font-weight:700">' + (order.car_number || '') + '</td></tr>'
      + '<tr><th class="lbl">매매금액</th><td style="font-size:14px;font-weight:700;color:#D85A30">₩ ' + priceStr + '</td>'
      + '<th class="lbl">연식</th><td>' + (order.car_year || '') + '년</td></tr>'
      + '<tr><th class="lbl">계약금</th><td></td>'
      + '<th class="lbl">잔금</th><td></td></tr>'
      + '</tbody></table>'
      + '<div style="font-size:10.5px;line-height:1.85;padding:0 2px">'
      + '<p style="margin:4px 0;text-indent:12px"><b>제1조</b> (당사자표시) 매도인을 "갑"이라 하고, 매수인을 "을"이라 한다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제2조</b> (하자담보책임) "을"은 "갑"으로부터 차량 인수 후 동 차량의 고장 또는 불량을 이유로 "갑"에게 그 책임을 물을 수 없다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제3조</b> (사고책임) "을"은 동 차량을 인수한 때부터 발생한 모든 사고에 대하여 책임을 진다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제4조</b> (위약금의 처리) "갑"이 동 차량계약을 위약하였을 경우는 "을"에게 계약금의 2배를 배상하고, "을"이 위약하였을 경우는 "갑"에게 계약금의 반환을 요구할 수 없다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제5조</b> (법률상 하자책임) 자동차 인도일 이전에 발생한 행정처분 또는 말소등록 요건의 하자 등으로 발생되는 문제에 대하여는 "갑"이 그 책임을 진다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제6조</b> (동시이행의무) "갑"은 잔금 지급과 말소등록에 필요한 서류와 매매 목적물을 "을"에게 인도하고 "을"은 인수 후 30일 이내에 수출용 자동차등록 임시말소를 신청해야 한다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제7조</b> (말소지체책임) 말소등록에 필요한 서류 등의 교부 또는 권한의 위임을 받은 "을"이 말소등록을 이행하지 않은 경우 이에 대한 모든 책임은 "을"이 진다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제8조</b> (위임장) "갑"은 동 차량의 말소등록에 필요한 권한을 "을"에게 위임한다.</p>'
      + '<p style="margin:4px 0;text-indent:12px"><b>제9조</b> (계약) 이 계약서는 매도인 1통, 매수인 1통, 세무서 제출용 1통으로 작성한다.</p>'
      + '</div>'
      + '<p style="text-align:center;font-size:10.5px;margin:10px 0">위 계약에 대하여 "갑"과 "을"은 이 계약서에 서명 날인한다.</p>'
      + '<div style="text-align:center;font-size:14px;margin:20px 0;letter-spacing:4px">'
      + yy + ' 년 &nbsp;&nbsp; ' + mo + ' 월 &nbsp;&nbsp; ' + dy + ' 일</div>'
      + '<div style="font-size:12px;font-weight:700;letter-spacing:2px;margin:14px 0 4px;border-bottom:1px solid #333;padding-bottom:3px">매도인 (갑)</div>'
      + '<table class="ptbl"><tbody>'
      + '<tr><th class="pl">성 명</th><td></td><td rowspan="3" style="width:80px;text-align:center">(인)</td></tr>'
      + '<tr><th class="pl">주민(사업자)등록번호</th><td></td></tr>'
      + '<tr><th class="pl">주 소</th><td></td></tr>'
      + '</tbody></table>'
      + '<div style="font-size:12px;font-weight:700;letter-spacing:2px;margin:12px 0 4px;border-bottom:1px solid #333;padding-bottom:3px">매수인 (을)</div>'
      + '<table class="ptbl"><tbody>'
      + '<tr><th class="pl">성 명</th><td></td><td rowspan="3" style="width:80px;text-align:center">(인)</td></tr>'
      + '<tr><th class="pl">주민(사업자)등록번호</th><td></td></tr>'
      + '<tr><th class="pl">주 소</th><td></td></tr>'
      + '</tbody></table>'
      + '<script>window.onload=function(){window.print()}<\/script>'
      + '</body></html>';
    var w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
    setShowContractForm(false);
    setContractPrice('');
  };

  const sectionStyle = {
    background: '#fff', borderRadius: 12, border: '1px solid #eeeee8',
    padding: 16, marginBottom: 12,
  };

  return (
    <div>
      {/* 상단 네비 */}
      <button onClick={onBack} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 14, color: '#3B8BD4', padding: 0, marginBottom: 14,
        fontWeight: 500,
      }}>← 오더 목록</button>

      {/* 차량 헤더 */}
      <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111' }}>{order.car_model}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
            {order.car_detail} · {order.car_number || '번호 미등록'}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {/* 기본 정보 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[
          ['연식 / 주행', `${order.car_year}년 / ${(order.car_mileage || 0).toLocaleString()}km`],
          ['색상 / 연료', `${order.car_color} / ${order.car_fuel}`],
          ['요청자', order.requester || '-'],
          ['판매자', order.seller_phone || '-'],
          ['위치', order.location || '-'],
          ['일정', `${order.inspect_date || '미정'} ${order.inspect_time || ''}`],
          ['검차비', formatMoney(order.fee)],
          ['검차원', order.inspector_name ? `${order.inspector_name} (${order.taken_at || ''})` : '콜 대기중'],
        ].map(([label, value], i) => (
          <div key={i} style={{
            background: '#f8f8f5', borderRadius: 8, padding: '10px 12px',
          }}>
            <div style={{ fontSize: 10, color: '#aaa', fontWeight: 500 }}>{label}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginTop: 2 }}>{value}</div>
          </div>
        ))}
      </div>

      {/* 상세 위치 */}
      {order.location_detail && (
        <div style={{ ...sectionStyle, padding: '10px 14px' }}>
          <span style={{ fontSize: 10, color: '#aaa' }}>상세위치</span>
          <div style={{ fontSize: 13, color: '#333', marginTop: 2 }}>{order.location_detail}</div>
        </div>
      )}

      {/* 판매예정일 경고 */}
      {order.sale_date && (
        <div style={{
          background: '#FFF8E1', borderRadius: 10, padding: '10px 14px',
          marginBottom: 12, fontSize: 13, color: '#7A6200',
          border: '1px solid #FFE082',
        }}>
          판매예정일: <strong>{order.sale_date}</strong> — 차주분과 시간 조율 필요
        </div>
      )}

      {/* 검차원 카드 */}
      {inspector && (
        <div style={{
          ...sectionStyle, display: 'flex', alignItems: 'center', gap: 12,
          cursor: 'pointer',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: inspector.avg_rating >= 4.5 ? '#FAECE7' : '#EEEDFE',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 16,
            color: inspector.avg_rating >= 4.5 ? '#D85A30' : '#534AB7',
          }}>
            {inspector.name?.[0]}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: '#222' }}>{inspector.name}</span>
              <LevelBadge level={inspector.level} />
            </div>
            <div style={{ fontSize: 11, color: '#888' }}>
              {inspector.area} · 평점 {inspector.avg_rating}★ · {inspector.total_jobs}건
            </div>
          </div>
        </div>
      )}

      {/* 메모 */}
      {order.notes && (
        <div style={{ ...sectionStyle, padding: '10px 14px' }}>
          <span style={{ fontSize: 10, color: '#aaa' }}>검차 메모</span>
          <div style={{ fontSize: 13, color: '#333', marginTop: 4, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {order.notes}
          </div>
        </div>
      )}

      {/* ── 평가 결과 ── */}
      {evalResult && (
        <div style={sectionStyle}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#222' }}>검차 평가</div>
          <div>
            {evalKeys.map((key, i) => (
              <div key={key} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '5px 0',
                borderBottom: i < evalKeys.length - 1 ? '1px solid #f0f0ec' : 'none',
              }}>
                <span style={{
                  fontSize: 13, color: key === 'score_overall' ? '#222' : '#888',
                  fontWeight: key === 'score_overall' ? 600 : 400,
                }}>{evalLabels[i]}</span>
                <Stars value={evalResult[key]} size={14} />
              </div>
            ))}
          </div>
          {(function() {
            var photos = evalResult && evalResult.photos;
            if (typeof photos === 'string') { try { photos = JSON.parse(photos); } catch(e) { photos = []; } }
            if (!Array.isArray(photos)) photos = [];
            photos = photos.filter(function(p) { return p.url && !p.url.startsWith('blob:'); });

            var CATEGORY_META = {
              exterior: { label: '외관', color: '#3B8BD4', bg: '#E6F1FB' },
              damage: { label: '사고부위', color: '#E24B4A', bg: '#FCEBEB' },
              interior: { label: '내관', color: '#1D9E75', bg: '#E1F5EE' },
              undercarriage: { label: '하부/엔진', color: '#BA7517', bg: '#FAEEDA' },
              documents: { label: '서류', color: '#534AB7', bg: '#EEEDFE' },
            };

            var byCategory = { exterior: [], damage: [], interior: [], undercarriage: [], documents: [] };
            photos.forEach(function(p, origIdx) {
              var cat = p.category || 'exterior';
              if (!byCategory[cat]) byCategory[cat] = [];
              byCategory[cat].push({ photo: p, origIdx: origIdx });
            });

            var totalPhotos = photos.length;

            var renderPhotoItem = function(entry, key) {
              var p = entry.photo;
              var origIndex = entry.origIdx;
              var isVideo = /\.(mp4|mov|avi|webm|3gp)$/i.test(p.filename || p.url || '');
              return (
                <div key={key} style={{ position: 'relative', cursor: 'pointer' }} onClick={function() { setViewPhotoIndex(origIndex); }}>
                  {isVideo
                    ? <video src={p.url} controls style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', background: '#000' }} />
                    : <img src={thumbBlobs[p.url] || ''} alt={p.memo || p.category} loading="lazy" decoding="async" width="400" height="400" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', background: '#f0f0ec' }} />
                  }
                  {p.memo && <div style={{ position: 'absolute', bottom: 3, left: 3, right: 24, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, padding: '2px 6px', borderRadius: 4, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{p.memo}</div>}
                  <button onClick={function(e){e.stopPropagation();window.open(p.url.replace(/\/uploads\/eval\/(\d+)\/(.+)/,'/api/eval/orders/$1/photos/$2/download'),'_blank')}} style={{ position: 'absolute', bottom: 3, right: 3, width: 22, height: 22, borderRadius: 4, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  </button>
                </div>
              );
            };

            var renderCategoryGrid = function(catKey, columns, showLabel) {
              var meta = CATEGORY_META[catKey];
              var items = byCategory[catKey] || [];
              return (
                <div key={catKey} style={{ marginBottom: 14 }}>
                  {showLabel !== false && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: meta.color, background: meta.bg, padding: '2px 8px', borderRadius: 5 }}>{meta.label}</span>
                      <span style={{ fontSize: 11, color: '#aaa' }}>{items.length}장</span>
                    </div>
                  )}
                  {items.length > 0 ? (
                    // Wave W4 (2026-05-21) — auto-fill + minmax 반응형. columns 는 데스크탑 max cap.
                    //   <320px 화면 → 자동 2col (사진 ~95px) / 큰 화면 → 최대 columns 개까지.
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(95px, 1fr))',
                      gap: 6,
                      maxWidth: columns ? (columns * 140) : '100%',
                    }}>
                      {items.map(function(entry, i) { return renderPhotoItem(entry, i); })}
                    </div>
                  ) : (
                    <div style={{ background: '#f8f8f5', borderRadius: 8, padding: '12px 0', textAlign: 'center', fontSize: 12, color: '#bbb' }}>미등록</div>
                  )}
                </div>
              );
            };

            var dmg = order.damageData || (evalResult && evalResult.damage_data) || null;
            if (dmg && typeof dmg === 'string') { try { dmg = JSON.parse(dmg); } catch(e) { dmg = null; } }
            var hasDmgMap = dmg && Object.keys(dmg).length > 0;
            var vehiclePhotoCount = byCategory.exterior.length + byCategory.interior.length + byCategory.undercarriage.length;

            return (
              <React.Fragment>
                {totalPhotos > 0 && (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#222' }}>검차 사진 ({totalPhotos}장)</div>
                    <button style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d5dd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#475569' }} onClick={function(){window.open(API_ORIGIN+'/api/eval/orders/'+order.id+'/photos/download-all','_blank')}}>📥 전체 다운로드</button>
                  </div>
                )}

                {/* 1. 도막 측정 */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#222' }}>도막 측정</div>
                  <EvalPaintSection data={evalResult} />
                </div>

                {/* 2. 사고 부위 — 맵 + 사고부위 카테고리 사진 (1-col 크게) */}
                {(hasDmgMap || byCategory.damage.length > 0) && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#222' }}>사고 부위</div>
                    {hasDmgMap && <EvalDamageSection value={dmg} />}
                    {byCategory.damage.length > 0 && (
                      <div style={{ marginTop: hasDmgMap ? 12 : 0 }}>
                        {renderCategoryGrid('damage', 3, false)}
                      </div>
                    )}
                  </div>
                )}

                {/* 3. 결과표 (서류 사진) */}
                {byCategory.documents.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#222' }}>결과표</div>
                    {renderCategoryGrid('documents', 3, false)}
                  </div>
                )}

                {/* 4. 사진 — 외관/내관/하부 */}
                {vehiclePhotoCount > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10, color: '#222' }}>사진</div>
                    {['exterior', 'interior', 'undercarriage'].map(function(catKey) {
                      if (byCategory[catKey].length === 0) return null;
                      return renderCategoryGrid(catKey, 3, true);
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })()}
        </div>
      )}

      {/* ── 기존 리뷰 표시 ── */}
      {inspectorReview && (
        <div style={{
          background: '#FAEEDA', borderRadius: 12, padding: 14, marginBottom: 12,
          border: '1px solid #FAC775',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#854F0B', marginBottom: 6 }}>
            평가사 리뷰 완료
          </div>
          <Stars value={inspectorReview.score_overall} size={16} />
          {inspectorReview.comment && (
            <div style={{ fontSize: 13, color: '#633806', marginTop: 6 }}>{inspectorReview.comment}</div>
          )}
          <div style={{ fontSize: 10, color: '#888', marginTop: 6, display: 'flex', gap: 10 }}>
            <span>신속 {inspectorReview.score_speed}★</span>
            <span>정확 {inspectorReview.score_accuracy}★</span>
            <span>꼼꼼 {inspectorReview.score_detail}★</span>
          </div>
        </div>
      )}

      {/* ── 액션 버튼들 ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {/* 매입 결정 */}
        {order.status === 'completed' && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowDecideModal(true)} style={{
              flex: 1, padding: '13px 0', borderRadius: 10, border: 'none',
              background: '#D85A30', color: '#fff',
              cursor: 'pointer', fontSize: 15, fontWeight: 700,
            }}>매입 결정 → AUTODEX 등록</button>
            {/* Wave W3 (2026-05-21) — 매입 불가 확정 전 확인 다이얼로그. 실수 클릭 차단. */}
            <button onClick={() => setModal({
              type: 'confirm',
              title: '매입 불가 확정',
              message: '이 차량을 매입 불가로 처리합니다. 평가사 작업 수수료는 정산되지만 차량 등록은 진행되지 않습니다.\n계속하시겠습니까?',
              confirmText: '불가 확정',
              onConfirm: function() {
                closeModal();
                onStatusChange(order.id, 'rejected');
              },
            })} style={{
              padding: '13px 16px', borderRadius: 10, background: '#fff',
              color: '#A32D2D', border: '1.5px solid #A32D2D',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
            }}>불가</button>
          </div>
        )}

        {/* 평가사 리뷰 */}
        {order.status === 'completed' && !inspectorReview && order.inspector_id && !showReviewForm && (
          <button onClick={() => setShowReviewForm(true)} style={{
            padding: '11px 0', borderRadius: 10, border: '1.5px solid #EF9F27',
            background: '#fff', color: '#BA7517', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
          }}>평가사 리뷰 작성 ★</button>
        )}

        {/* 리뷰 폼 */}
        {showReviewForm && (
          <div style={{
            background: '#fff', borderRadius: 12, border: '2px solid #EF9F27', padding: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#854F0B', marginBottom: 12 }}>평가사 리뷰</div>
            {[
              ['speed', '신속성', '일정 준수, 응답 속도'],
              ['accuracy', '정확성', '도막 측정, 사고 판단 정확도'],
              ['detail', '꼼꼼함', '사진 수량/품질, 메모 상세도'],
              ['overall', '종합 *', '전반적인 만족도'],
            ].map(([key, label, desc]) => (
              <div key={key} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0',
                borderTop: key === 'overall' ? '1px solid #f0f0ec' : 'none',
                marginTop: key === 'overall' ? 4 : 0,
              }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: key === 'overall' ? 600 : 400, color: '#333' }}>{label}</span>
                  <div style={{ fontSize: 10, color: '#aaa' }}>{desc}</div>
                </div>
                <Stars value={reviewForm[key]} size={key === 'overall' ? 22 : 18}
                  onChange={v => setReviewForm(p => ({ ...p, [key]: v }))} />
              </div>
            ))}
            <textarea value={reviewForm.comment}
              onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))}
              rows={2} placeholder="코멘트 (선택)"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e0dfda', borderRadius: 6, fontSize: 13, marginTop: 10, marginBottom: 12, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowReviewForm(false)} style={{
                padding: '9px 16px', borderRadius: 8, border: '1px solid #d0d0c8',
                background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555',
              }}>나중에</button>
              <button onClick={handleReview} style={{
                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                background: reviewForm.overall > 0 ? '#EF9F27' : '#ccc',
                color: '#fff', cursor: reviewForm.overall > 0 ? 'pointer' : 'default',
                fontSize: 14, fontWeight: 700,
              }}>리뷰 제출</button>
            </div>
          </div>
        )}

        {/* 매입 완료 상태 */}
        {order.status === 'decided' && (
          <div style={{
            background: '#FAECE7', borderRadius: 10, padding: '14px 16px',
            textAlign: 'center', border: '1px solid #F5C4B3',
          }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#993C1D' }}>
              AUTODEX 차량 등록 완료
            </span>
            {order.purchase_price && (
              <div style={{ fontSize: 12, color: '#712B13', marginTop: 4 }}>
                매입가: {formatMoney(order.purchase_price)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 사진 확대 모달 — PhotoLightbox로 분리 */}
      <PhotoLightbox photos={allPhotos} viewIndex={viewPhotoIndex} setViewIndex={setViewPhotoIndex} />
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
      {showDecideModal && (
        <EvalDecideModal order={order} onClose={() => setShowDecideModal(false)} onSuccess={handleDecideSuccess} />
      )}
    </div>
  );
}
